/* Xiaomi 2026 · Drag-drop media upload for feedback capture
 *
 * Usage:
 *   <MediaUploadDropzone
 *      dealerId="200167"
 *      kind="dinner_photo"          // see VALID_MEDIA_KINDS in backend
 *      onUploaded={(media) => append(media)}
 *      onError={(err) => toast.error(err)}
 *   />
 *
 * Behaviour:
 *   - Drag-drop or click-to-pick · multi-file supported
 *   - Accepts images AND videos (the backend Cloudinary call uses resource_type=auto)
 *   - Shows per-file upload progress
 *   - On success, calls onUploaded(media) with the FULL backend response shape
 *     {url, public_id, resource_type, kind, width, height, duration_sec,
 *      thumbnail_url, bytes, format}
 *   - On error, calls onError(string)
 *   - 50MB per file safety cap (Cloudinary free tier comfortable)
 */
import React, { useCallback, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEDIA_KINDS = [
  { value: 'dinner_photo', label: 'Dinner photo' },
  { value: 'dinner_video', label: 'Dinner video' },
  { value: 'gift_receipt_photo', label: 'Gift receipt' },
  { value: 'family_photo', label: 'Family' },
  { value: 'staycation_photo', label: 'Staycation photo' },
  { value: 'staycation_video', label: 'Staycation video' },
  { value: 'dayout_photo', label: 'Day-out' },
  { value: 'other', label: 'Other' },
];

const MAX_BYTES = 50 * 1024 * 1024; // 50 MB cap

export default function MediaUploadDropzone({
  dealerId,
  kind: defaultKind = 'other',
  onUploaded,
  onError,
}) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState([]); // array of {name, progress}
  const [kind, setKind] = useState(defaultKind);
  const inputRef = useRef(null);

  const auth = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  const handleFiles = useCallback(async (files) => {
    const list = Array.from(files);
    if (!list.length) return;
    const oversized = list.filter((f) => f.size > MAX_BYTES);
    if (oversized.length) {
      onError?.(`File too large (>50MB): ${oversized.map((f) => f.name).join(', ')}`);
      return;
    }
    for (const file of list) {
      const trackingEntry = { name: file.name, progress: 0 };
      setUploading((prev) => [...prev, trackingEntry]);
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('dealer_id', dealerId);
        fd.append('kind', kind);
        const r = await axios.post(
          `${API}/xiaomi/feedback/media-upload`,
          fd,
          {
            ...auth(),
            headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (e) => {
              if (e.total) {
                const pct = Math.round((e.loaded / e.total) * 100);
                setUploading((prev) =>
                  prev.map((u) => (u.name === file.name ? { ...u, progress: pct } : u)),
                );
              }
            },
          },
        );
        onUploaded?.(r.data);
      } catch (e) {
        onError?.(e?.response?.data?.detail || `Upload failed: ${file.name}`);
      } finally {
        setUploading((prev) => prev.filter((u) => u.name !== file.name));
      }
    }
  }, [dealerId, kind, onError, onUploaded]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const onPickClick = () => inputRef.current?.click();

  return (
    <div data-testid="media-upload-dropzone">
      <div className="flex items-center justify-between mb-2">
        <label className="text-[11px] uppercase tracking-wider text-soft">
          Media kind
        </label>
        <select
          value={kind} onChange={(e) => setKind(e.target.value)}
          className="text-[12px] px-2 py-1 border border-line rounded bg-white"
          data-testid="media-kind-select"
        >
          {MEDIA_KINDS.map((k) => (
            <option key={k.value} value={k.value}>{k.label}</option>
          ))}
        </select>
      </div>
      <button
        type="button"
        onClick={onPickClick}
        onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`w-full border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragging
            ? 'border-[#ff6900] bg-[#fff7ed]'
            : 'border-line bg-[#faf6ef] hover:bg-[#fff7ed]'
        }`}
        data-testid="dropzone-trigger"
      >
        <div className="text-2xl mb-1">📸</div>
        <div className="text-sm font-medium text-[#15110d]">
          {dragging ? 'Drop to upload' : 'Drag photos or videos here'}
        </div>
        <div className="text-[11px] text-soft mt-0.5">
          or click to pick · jpeg · png · mp4 · mov · ≤ 50 MB each
        </div>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="dropzone-file-input"
      />
      {uploading.length > 0 && (
        <div className="mt-2 space-y-1" data-testid="dropzone-progress-list">
          {uploading.map((u) => (
            <div key={u.name} className="text-[11px] flex items-center gap-2">
              <span className="truncate flex-1">{u.name}</span>
              <div className="w-24 h-1 rounded bg-[#e5e7eb] overflow-hidden">
                <div
                  className="h-full bg-[#ff6900] transition-all"
                  style={{ width: `${u.progress}%` }}
                />
              </div>
              <span className="text-soft">{u.progress}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
